import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelExtrusion } from "building-types/levels/levelPerimeters";
import { FaceCategory } from "building-types/materials/faceCategories";
import { RailingInfillStyle, type GeneratedRailing, type RailingGeomTag } from "building-types/settings/railingTypes";
import type { Table } from "common/data/table";
import { faceCentroid } from "geom/brep";
import centroidInsideLevelExtrusions from "../utils/centroidInsideLevelExtrusions";
import { buildExteriorLayers, buildInteriorLayers } from "./buildingGeometryTools";

function railingComponentName(tag: RailingGeomTag): string {
  if (tag.type === "POST") {
    return "Post";
  }
  if (tag.type === "RAIL") {
    return tag.railType === "TOP" ? "Top_Rail" : "Bottom_Rail";
  }
  return tag.infillType === RailingInfillStyle.BALUSTERS ? "Baluster" : "Solid_Infill";
}

export function getRailingGeometries(
  railings: Table<GeneratedRailing>,
  levelExtrusions: Table<LevelExtrusion> | undefined,
  emitter: BuildingGeometryEmitter,
): void {
  for (const railingId in railings) {
    const railing = railings[railingId];
    const { groups, levelId, railingPathFacesForBuildingOutlineAndLighting } = railing;
    emitter.beginGroup?.("Railing", railingId);

    let exteriorLayers = true;
    if (levelExtrusions) {
      let centroidCount = 0;
      let usableCount = 0;
      for (const face of railingPathFacesForBuildingOutlineAndLighting) {
        const centroid = faceCentroid(face);
        if (centroid) {
          centroidCount++;
          const inside = centroidInsideLevelExtrusions(levelExtrusions, centroid);
          if (inside !== undefined) {
            usableCount++;
          }
          if (inside) {
            exteriorLayers = false;
            break;
          }
        }
      }
      // Every path face this railing had was degenerate, so the test never really ran. Treat that as
      // interior rather than inheriting "exterior" from a NaN comparison.
      if (centroidCount > 0 && usableCount === 0) {
        exteriorLayers = false;
      }
    }

    let entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
    if (railing.instanceId) {
      entityHandleForSelectingReportingUnit = { type: "COMPONENT_INSTANCE", id: railing.instanceId };
    } else {
      entityHandleForSelectingReportingUnit = { type: "RAILING", id: railingId };
    }

    for (const { group: geomGroup, pickHandle } of groups) {
      const { geom: geomArray, tags } = geomGroup;

      for (let i = 0, l = geomArray.length; i < l; i++) {
        const geom = geomArray[i];
        const { materialSrc, uvGen, viewEntityDescriptor } = tags[i];

        emitter.emit({
          geom,
          faceCategory: FaceCategory.RAILING,
          exportComponentName: railingComponentName(tags[i]),
          levelId,
          pickHandle,
          materialSrc,
          uvGen,
          entityId: viewEntityDescriptor?.entityId,
          entityHandleForSelectingReportingUnit,
          descriptor: viewEntityDescriptor,
          threeLayers: exteriorLayers ? buildExteriorLayers() : buildInteriorLayers(),
        });
      }
    }

    emitter.endGroup?.("Railing");
  }
}
